/**
 * Pure helpers for deriving filter options, formatting job fields,
 * and filtering the job list. Operate on the view-model produced by
 * jobsAdapter (fields: title, department, jobType, locations[], ...), so they
 * stay decoupled from the raw Keka/internal-API wire shape. Kept
 * framework-agnostic so they can be unit-tested.
 */
import {
  JOB_TYPE_MAP,
  DEFAULT_JOB_TYPE_LABEL,
  NO_LOCATION_LABEL,
  REMOTE_LABEL,
  LOCATION_SEPARATOR,
} from "./jobsConstants";

/**
 * Human-readable employment type. The internal API sends a label string
 * (e.g. "Full Time") which is used as-is; a legacy numeric code is mapped via
 * JOB_TYPE_MAP; anything empty falls back to the default label.
 */
export const getJobTypeLabel = (jobType) =>
  JOB_TYPE_MAP[jobType] || jobType || DEFAULT_JOB_TYPE_LABEL;

/** City name for a single location object (city, falling back to name). */
const getCityName = (loc) => (loc && (loc.city || loc.name)) || "";

/** "City, State" for a single location; just "City" when state is absent. */
const formatLocation = (loc) => {
  const city = getCityName(loc);
  const state = (loc && loc.state) || "";
  if (city && state) return `${city}, ${state}`;
  return city || state;
};

/**
 * The Location-filter bucket(s) a job belongs to:
 * - no locations           -> ["Location Not Specified"]
 * - a location named Remote -> "Remote" (instead of a city)
 * - otherwise               -> its distinct city names
 * Used for both building the Location options and matching the Location filter.
 */
export const getJobCities = (job) => {
  const locations = Array.isArray(job.locations) ? job.locations : [];
  if (locations.length === 0) return [NO_LOCATION_LABEL];
  const values = new Set();
  locations.forEach((loc) => {
    const city = getCityName(loc).trim();
    if (!city) return;
    if (/^remote$/i.test(city)) values.add(REMOTE_LABEL);
    else values.add(city);
  });
  return values.size > 0 ? Array.from(values) : [NO_LOCATION_LABEL];
};

/**
 * Label shown on a job card's location row (PRD FR-4).
 * - no locations       -> "Location not specified" (we don't assume remote)
 * - one location       -> "City, State" (state appended only if present)
 * - many locations     -> every location joined by " · ",
 *                         e.g. "Kolkata, West Bengal · Pune, Maharashtra"
 */
export const getJobLocationLabel = (job) => {
  const locations = Array.isArray(job.locations) ? job.locations : [];
  const parts = locations.map(formatLocation).filter(Boolean);
  if (parts.length === 0) return NO_LOCATION_LABEL;
  return parts.join(LOCATION_SEPARATOR);
};

/**
 * Build a sorted list of filter options with counts.
 * `valuesFor` returns the values a single job contributes to this filter.
 * Count = number of jobs that contribute the value.
 */
const buildOptions = (jobs, valuesFor) => {
  const counts = new Map();
  jobs.forEach((job) => {
    const seen = new Set(valuesFor(job));
    seen.forEach((value) => {
      counts.set(value, (counts.get(value) || 0) + 1);
    });
  });
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, label: value, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// "Remote" pins to the top; "Location Not Specified" pins to the bottom. Both,
// like any other option, only appear when they actually have matching jobs.
const TOP_LOCATIONS = [REMOTE_LABEL];
const BOTTOM_LOCATIONS = [NO_LOCATION_LABEL];

/**
 * Keep every currently-selected value in the list, even when the OTHER active
 * filters give it a 0 count. Without this a selected option can disappear from
 * its group while staying active — silently zeroing the results with no way to
 * see or undo it. Missing selected values are added with count 0.
 */
const ensureSelected = (options, selected) => {
  const present = new Set(options.map((o) => o.value));
  const missing = (selected || [])
    .filter((value) => !present.has(value))
    .map((value) => ({ value, label: value, count: 0 }));
  return [...options, ...missing].sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Location filter options with counts: "Remote" first, then the cities in
 * alphabetical order, then "Location Not Specified" last. Options with no
 * matching jobs are omitted — except currently-selected ones, which always show
 * (at count 0 if needed) so they stay removable.
 */
export const buildLocationOptions = (jobs, selected = []) => {
  const options = ensureSelected(buildOptions(jobs, getJobCities), selected);
  const pick = (value) => options.find((o) => o.value === value);
  const top = TOP_LOCATIONS.map(pick).filter(Boolean);
  const bottom = BOTTOM_LOCATIONS.map(pick).filter(Boolean);
  const special = new Set([...TOP_LOCATIONS, ...BOTTOM_LOCATIONS]);
  const rest = options.filter((o) => !special.has(o.value));
  return [...top, ...rest, ...bottom];
};

/**
 * Department filter options, built from the mapped `department` field. Jobs with
 * a null/empty department contribute nothing. Currently-selected departments
 * always show (at count 0 if needed) so they stay removable.
 */
export const buildDepartmentOptions = (jobs, selected = []) =>
  ensureSelected(
    buildOptions(jobs, (job) => (job.department ? [job.department] : [])),
    selected
  );

/**
 * Filter jobs by free-text search (title) + selected locations + selected
 * departments. A job passes a filter group when nothing in that group is
 * selected, or when it matches at least one selected value (OR within a
 * group, AND across groups).
 */
export const filterJobs = (jobs, { search, locations, departments }) => {
  const query = (search || "").trim().toLowerCase();
  const selectedLocations = locations || [];
  const selectedDepartments = departments || [];

  return jobs.filter((job) => {
    const matchesSearch =
      !query || (job.title || "").toLowerCase().includes(query);

    const matchesLocation =
      selectedLocations.length === 0 ||
      getJobCities(job).some((city) => selectedLocations.includes(city));

    const matchesDepartment =
      selectedDepartments.length === 0 ||
      (job.department && selectedDepartments.includes(job.department));

    return matchesSearch && matchesLocation && matchesDepartment;
  });
};
